/**
 * completion-log-ducks — Redux-side delta channel for the
 * CompletionLog component's locally-paginated list.
 *
 * The log itself is fetched + paginated inside CompletionLog.tsx
 * (component state), so we don't mirror the entire stream in Redux.
 * What we DO mirror is the side-effects of sagas the user just ran
 * (mark-blocked added an entry, unblock removed one). Components
 * subscribe to `selectCompletionLogDelta` and apply the delta to
 * their local list — keeping the UI consistent without a refetch.
 *
 * Pext-style discipline: each saga that touches completion log
 * yields one of these actions. The component is the single consumer
 * and decides how to merge into its local view.
 */

import type { TodoCompletion } from '@/infra/types/domain';

export const PREFIX = 'completionLog';
const ROW_CHANGED = `${PREFIX}/rowChanged`;

export type CompletionLogDelta =
  | { type: 'added'; completion: TodoCompletion; seq: number }
  | { type: 'removed'; id: string; seq: number };

interface CompletionLogState {
  // Latest delta. Components watch this and apply on change. `seq`
  // increments on every dispatch so even identical-payload deltas
  // (rare but possible) trigger a re-render.
  lastDelta: CompletionLogDelta | null;
}

const initialState: CompletionLogState = { lastDelta: null };

export function completionLogRowChanged(
  delta:
    | { type: 'added'; completion: TodoCompletion }
    | { type: 'removed'; id: string },
) {
  return { type: ROW_CHANGED, payload: delta } as const;
}

export function completionLogReducer(
  state: CompletionLogState = initialState,
  action: { type: string; payload?: unknown },
): CompletionLogState {
  if (action.type === ROW_CHANGED) {
    const seq = (state.lastDelta?.seq ?? 0) + 1;
    return {
      lastDelta: { ...(action.payload as object), seq } as CompletionLogDelta,
    };
  }
  return state;
}

export function selectCompletionLogDelta(state: {
  completionLog?: CompletionLogState;
}): CompletionLogDelta | null {
  return state.completionLog?.lastDelta ?? null;
}
